import { Component, computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import Keycloak from 'keycloak-js';
import { RoleAccess } from 'src/app/rgs/components/company-data/role/models/role-access.model';
import { RoleModuleKey } from 'src/app/rgs/components/company-data/role/models/role-module-key.model';
import { UserComplete } from 'src/app/rgs/components/company-data/user/models/user-complete.model';
import { PermissionsService } from 'src/app/shared/services/permissions.service';
import { ArrayUtils } from 'src/app/shared/utils/array.utils';
import { AppMenuitemComponent } from '../menu-item/app.menuitem.component';
import { AppMenuItem } from '../models/app-menu-item.model';

@Component({
  selector: 'app-menu',
  templateUrl: './app.menu.component.html',
  imports: [AppMenuitemComponent],
})
export class AppMenuComponent {
  private readonly keycloak = inject(Keycloak);
  private readonly permissionsService = inject(PermissionsService);

  private readonly user = toSignal(this.permissionsService.user$);

  public readonly model = computed<AppMenuItem[]>(() => {
    const user = this.user();

    if (!user) {
      return [];
    }

    return this.userRoles.includes('admin') ? this.adminMenus : this.getUserMenus(user);
  });

  public get userRoles() {
    const tokenParsed = this.keycloak.tokenParsed;
    const roles = ArrayUtils.ensureArray(tokenParsed?.realm_access?.roles);
    return roles;
  }

  public get adminMenus() {
    return [
      {
        label: 'sidebar.admin',
        icon: 'fa fa-cogs fa-fw',
        items: [
          {
            label: 'sidebar.users',
            icon: 'fa fa-users fa-fw',
            routerLink: ['/admin/users'],
          },
          {
            label: 'sidebar.roles',
            icon: 'fa fa-unlock fa-fw',
            routerLink: ['/admin/roles'],
          },
          {
            label: 'sidebar.modules',
            icon: 'fa fa-cubes fa-fw',
            routerLink: ['/admin/modules'],
          },
          {
            label: 'sidebar.licenses',
            icon: 'fa fa-id-card fa-fw',
            routerLink: ['/admin/licenses'],
          },
          {
            label: 'sidebar.companies',
            icon: 'fa fa-industry fa-fw',
            routerLink: ['/admin/companies'],
          },
          {
            label: 'sidebar.wiki',
            icon: 'fa fa-book fa-fw',
            routerLink: ['/admin/wiki'],
          },
          {
            label: 'sidebar.reportTasks',
            icon: 'fa fa-tasks fa-fw',
            routerLink: ['/admin/report-tasks'],
          },
        ],
      },
      { separator: true },
      {
        label: 'sidebar.masterData',
        icon: 'fa fa-database fa-fw',
        items: [
          {
            label: 'sidebar.strategy',
            icon: 'fa fa-chess-knight fa-fw',
            routerLink: ['/admin/strategy'],
          },
          {
            label: 'Label',
            icon: 'fa fa-dw fa-tags fa-fw',
            items: [
              {
                label: 'sidebar.objectCharacteristics',
                icon: 'fa fa-dw fa-tags fa-fw',
                routerLink: ['/admin/object-category'],
              },
              {
                label: 'sidebar.layerFeatures',
                icon: 'fa fa-dw fa-tags fa-fw',
                routerLink: ['/admin/layer-features'],
              },
              {
                label: 'sidebar.templates',
                icon: 'fa fa-dw fa-file fa-fw',
                routerLink: ['/admin/documentcode'],
              },
            ],
          },
          {
            label: 'sidebar.elements',
            icon: 'fa fa-cube fa-fw',
            items: [
              {
                label: 'sidebar.groups',
                icon: 'fa fa-cubes fa-fw',
                routerLink: ['/admin/elementgroup'],
              },
              {
                label: 'sidebar.elements',
                icon: 'fa fa-cube fa-fw',
                routerLink: ['/admin/element'],
              },
            ],
          },
          {
            label: 'sidebar.inspection',
            icon: 'fa fa-eye fa-list fa-fw',
            items: [
              {
                label: 'sidebar.inspectionList',
                icon: 'fa fa-eye fa-fw',
                routerLink: ['/admin/inspection-list-list'],
              },
              {
                label: 'sidebar.method',
                icon: 'fa fa-eye fa-fw',
                routerLink: ['/admin/inspection-method-list'],
              },
            ],
          },
          {
            label: 'Scenario',
            icon: 'fas fa-code-branch fa-fw',
            items: [
              {
                label: 'sidebar.pricebook',
                icon: 'fa fa-wallet fa-fw',
                routerLink: ['/admin/pricebook'],
              },
            ],
          },
        ],
      },
    ];
  }

  public getUserMenus(user: UserComplete | undefined): AppMenuItem[] {
    const menus: AppMenuItem[] = [...this.getProjectDataMenus(user), ...this.getCompanyMenus(user)];

    if (this.permissionsService.hasModulePermission('masterdata', RoleAccess.READ, user)) {
      menus.push(...this.masterdataMenus);
    }

    const kibMenus = this.kibMenus;
    if (kibMenus.length > 0) {
      if (menus.length > 0) {
        menus.push({ separator: true });
      }
      menus.push(...kibMenus);
    }

    return menus;
  }

  /** Keeps the items whose module the user may read, in their original order. */
  private getReadableModuleItems(
    user: UserComplete | undefined,
    items: { module: RoleModuleKey; item: AppMenuItem }[],
  ): AppMenuItem[] {
    return items
      .filter(({ module }) => this.permissionsService.hasModulePermission(module, RoleAccess.READ, user))
      .map(({ item }) => item);
  }

  private getProjectDataMenus(user: UserComplete | undefined): AppMenuItem[] {
    if (ArrayUtils.ensureArray(user?.license).length === 0) {
      return [];
    }

    const menuItems = this.getReadableModuleItems(user, [
      {
        module: 'objects',
        item: { label: 'sidebar.objects', icon: 'fa fa-buildings fa-fw', routerLink: ['/objects'] },
      },
      {
        module: 'inspections',
        item: { label: 'collections.inspections.plural', icon: 'fa-solid fa-eye fa-fw', routerLink: ['/inspections'] },
      },
      {
        module: 'scenarios',
        item: {
          label: 'collections.scenarios.plural',
          icon: 'fa-solid fa-code-branch fa-fw',
          routerLink: ['/scenarios'],
        },
      },
      {
        module: 'dataAnalysis',
        item: { label: 'sidebar.dataAnalysis', icon: 'fa-solid fa-chart-pie fa-fw', routerLink: ['/data-analysis'] },
      },
      {
        module: 'tasks',
        item: { label: 'sidebar.tasks', icon: 'fa-solid fa-list-check fa-fw', routerLink: ['/tasks'] },
      },
    ]);

    return menuItems.length > 0 ? [{ label: 'sidebar.projectData', icon: 'fa fa-tasks fa-fw', items: menuItems }] : [];
  }

  private getCompanyMenus(user: UserComplete | undefined): AppMenuItem[] {
    const companyItems = this.getReadableModuleItems(user, [
      {
        module: 'companyProfile',
        item: { label: 'sidebar.companyProfile', icon: 'fa fa-id-card fa-fw', routerLink: ['/companyProfile'] },
      },
      { module: 'clients', item: { label: 'sidebar.clients', icon: 'fa fa-users fa-fw', routerLink: ['/clients'] } },
      { module: 'users', item: { label: 'sidebar.user', icon: 'fa fa-user fa-fw', routerLink: ['/users'] } },
      { module: 'roles', item: { label: 'sidebar.roles', icon: 'fa fa-unlock fa-fw', routerLink: ['/roles'] } },
    ]);

    if (companyItems.length === 0) {
      return [];
    }

    return [{ separator: true }, { label: 'sidebar.companyData', icon: 'fa fa-building fa-fw', items: companyItems }];
  }

  private get masterdataMenus(): AppMenuItem[] {
    return [
      { separator: true },
      {
        label: 'sidebar.masterData',
        icon: 'fa fa-database fa-fw',
        routerLink: ['/master'],
        items: [
          {
            label: 'sidebar.strategy',
            icon: 'fa fa-chess-knight fa-fw',
            routerLink: ['/master/strategy'],
          },
          {
            label: 'Label',
            icon: 'fa fa-dw fa-tags fa-fw',
            items: [
              {
                label: 'sidebar.objectCharacteristics',
                icon: 'fa fa-dw fa-tags fa-fw',
                routerLink: ['/master/object-category'],
              },
              {
                label: 'sidebar.layerFeatures',
                icon: 'fa fa-dw fa-tags fa-fw',
                routerLink: ['/master/layer-features'],
              },
              {
                label: 'sidebar.templates',
                icon: 'fa fa-dw fa-file fa-fw',
                routerLink: ['/master/documentcode'],
              },
            ],
          },
          {
            label: 'sidebar.elements',
            icon: 'fa fa-cube fa-fw',
            items: [
              {
                label: 'sidebar.groups',
                icon: 'fa fa-cubes fa-fw',
                routerLink: ['/master/elementgroup'],
              },
              {
                label: 'sidebar.elements',
                icon: 'fa fa-cube fa-fw',
                routerLink: ['/master/element'],
              },
            ],
          },
          {
            label: 'sidebar.inspection',
            icon: 'fa fa-eye fa-list fa-fw',
            items: [
              {
                label: 'sidebar.inspectionList',
                icon: 'fa fa-eye fa-fw',
                routerLink: ['/master/inspection-list-list'],
              },
              {
                label: 'sidebar.method',
                icon: 'fa fa-eye fa-fw',
                routerLink: ['/master/inspection-method-list'],
              },
            ],
          },
          {
            label: 'Scenario',
            icon: 'fas fa-code-branch fa-fw',
            items: [
              {
                label: 'sidebar.measures',
                icon: 'fa fa-wrench fa-fw',
                routerLink: ['/master/measures'],
              },
              {
                label: 'sidebar.pricebook',
                icon: 'fa fa-wallet fa-fw',
                routerLink: ['/master/pricebook'],
              },
            ],
          },
        ],
      },
    ];
  }

  private get kibMenus(): AppMenuItem[] {
    const userRoles = this.permissionsService.userRoles;
    if (!userRoles.includes('kib_admin') && !userRoles.includes('kib_user')) {
      return [];
    }

    const kibItems: AppMenuItem[] = [
      {
        label: 'sidebar.kibItems.inspectionList',
        icon: 'fa fa-eye fa-fw',
        routerLink: ['/kib/inspection-list-list'],
      },
      // {
      //   label: 'sidebar.method',
      //   icon: 'fa fa-eye fa-fw',
      //   routerLink: ['/kib/inspection-method-list'],
      // },
    ];

    if (userRoles.includes('kib_admin')) {
      kibItems.push(
        {
          label: 'sidebar.apiAccount',
          icon: 'fa fa-key fa-fw',
          routerLink: ['/kib/api-account-list'],
        },
        {
          label: 'sidebar.users',
          icon: 'fa fa-users fa-fw',
          routerLink: ['/kib/user-list'],
        },
      );
    }

    return [{ label: 'sidebar.kib', icon: 'fa fa-database fa-fw', items: kibItems }];
  }
}
